import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'retailcliq_global_context';

const defaultContext = {
  userId: '',
  username: '',
  groupId: '',
  roleId: '',
  companyId: '',
  branchId: '',
  role: '',
  company: null,
  branches: [],
  theme: { mode: 'light' },
  activeBranchId: '',
  adminFeatures: [],
};

const ERPContext = createContext(null);

export const useERPContext = () => {
  const context = useContext(ERPContext);
  if (!context) {
    throw new Error('useERPContext must be used within an ERPContextProvider');
  }
  return context;
};

// Safe version that returns defaults when outside provider
export const useERPContextSafe = () => {
  const context = useContext(ERPContext);
  return context || { ...defaultContext, setActiveBranch: () => {}, updateContext: () => {}, clearContext: () => {} };
};

export const ERPContextProvider = ({ children, initialContext }) => {
  const [globalCtx, setGlobalCtx] = useState(() => {
    // Try to restore from localStorage first, then use initial context
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultContext, ...parsed, ...(initialContext || {}) };
      }
    } catch (e) { /* ignore */ }
    return { ...defaultContext, ...(initialContext || {}) };
  });

  // Persist to localStorage whenever context changes
  useEffect(() => {
    if (globalCtx && globalCtx.userId) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(globalCtx));
    }
  }, [globalCtx]);

  // Set active branch (for branch switching)
  const setActiveBranch = useCallback((branchId) => {
    setGlobalCtx(prev => ({ ...prev, activeBranchId: branchId }));
  }, []);

  // Update arbitrary context fields
  const updateContext = useCallback((updates) => {
    setGlobalCtx(prev => ({ ...prev, ...updates }));
  }, []);

  // Clear on logout
  const clearContext = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setGlobalCtx(defaultContext);
  }, []);

  const value = {
    ...globalCtx,
    setActiveBranch,
    updateContext,
    clearContext,
    // Computed: is user allowed all branches?
    // ADMINSUPPORT/LICENSOR scoped users have access within their own company's branches
    hasAllBranchAccess: globalCtx.role === 'SUPER_ADMIN' || globalCtx.groupId === 'MAdmin' ||
      globalCtx.role === 'ADMINSUPPORT' || globalCtx.role === 'LICENSOR',
    // Current active branch (fallback to default)
    currentBranch: globalCtx.branches?.find(b => b.id === (globalCtx.activeBranchId || globalCtx.branchId)) || globalCtx.branches?.[0] || null,
  };

  return (
    <ERPContext.Provider value={value}>
      {children}
    </ERPContext.Provider>
  );
};

export default ERPContext;
